import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import {
  LoadSessions,
  SessionActionTypes,
  LoadSessionsSuccess,
  LoginSessions,
  LoginSessionsSuccess,
  LogoutSessions,
  LogoutSessionsSuccess,
  LoginSessionsFail,
  LogoutSessionsFail
} from './session.actions';
import { map, catchError, tap, switchMap, take } from 'rxjs/operators';
import { CoreState, Session } from './_state.interfaces';
import { CloseSideNav } from './nav.actions';
import { MENU_DEFINITION, HOME_URL } from 'src/app/app.config';
import { AuthService, AuthData } from 'src/app/services/api/auth.service';
import { getSession, getAdminFlag } from './_state.selectors';
import { APIResponse } from 'src/app/services/api/api.interfaces';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class SessionEffects {

  constructor(
    private actions$: Actions,
    private router: Router,
    private core: Store<CoreState>,
    private auth: AuthService
  ) {}

  @Effect()
  loadSession$: Observable<Action> =
    this.actions$.pipe(
      ofType<LoadSessions>(SessionActionTypes.LoadSessions),
      switchMap(
        () => this.core.select(getSession).pipe(
          take(1),
          catchError((err) => {    // ※catchErrorはaction$のpipe直下に入れてはいけない(エラー発生時にEffectが実行されなくなる)
            console.warn(err);
            return of<Session>({
              login: false,
              token: null
            });
          })
        )
      ),
      map(session => {
        if (!session.token) {
          // ログイン情報なし
          this.router.navigate(['login']);
          return new LoadSessionsSuccess({ session: { login: false, token: null }, menu: [] });
        } else {
          // ログイン情報あり
          return new LoadSessionsSuccess({ session: { login: true, token: session.token }, menu: MENU_DEFINITION});
        }})
    );

  @Effect()
  loginSession$: Observable<Action> =
    this.actions$.pipe(
      ofType<LoginSessions>(SessionActionTypes.LoginSessions),
      switchMap(
        action => {
          return this.auth.login(action.payload.userId, action.payload.password)
          .pipe(
            catchError((err) => {          // ※catchErrorはaction$のpipe直下に入れてはいけない(エラー発生時にEffectが実行されなくなる)
              console.warn(err);
              if (err.status === 401) {
                return of<APIResponse<AuthData[]>>({
                  result_code: '999',
                  error_message: '認証情報が正しくありません。もう一度試してください。',
                  data: null
                });
              } else {
                return of<APIResponse<AuthData[]>>({
                  result_code: '999',
                  error_message: 'ログイン中にエラーが発生しました。',
                  data: null
                });
              }

            })
          );
        }
      ),
      map(response => {
        if (response.result_code !== '00') {
          return new LoginSessionsFail({error: response.error_message});
        }
        const token = response.data[0].token;
        const helper = new JwtHelperService();
        const adminFlag: boolean = helper.decodeToken(token).user_claims.admin;
        const user_id: string = helper.decodeToken(token).user_claims.user_id;
        this.router.navigate([HOME_URL]);
        return new LoginSessionsSuccess({ session: { login: true, token: token }, menu: MENU_DEFINITION, adminFlag: adminFlag, userName: user_id });
      }),
    );

  @Effect()
  logoutSession$: Observable<Action> =
    this.actions$.pipe(
      ofType<LogoutSessions>(SessionActionTypes.LogoutSessions),
      switchMap(
        action => this.auth.logout().pipe(
          catchError((err) => {    // ※catchErrorはaction$のpipe直下に入れてはいけない(エラー発生時にEffectが実行されなくなる)
            console.warn(err);
            return of<APIResponse<{}>>({
              result_code: '999',
              error_message: 'ログアウト中にエラーが発生しました',
              data: null
            });
          })
        )
      ),
      map(response => {
        if (response.result_code !== '00') {
          return new LogoutSessionsFail({error: response.error_message});
        }
        this.router.navigate(['/login']);
        return new LogoutSessionsSuccess();
      })
    );

  // ログアウト成功時のステートクリア
  @Effect({dispatch: false})
  logoutSessionsSuccess$: Observable<Action> =
    this.actions$.pipe(
      ofType<LogoutSessions>(SessionActionTypes.LogoutSessionsSuccess),
      tap(() => {
        this.core.dispatch(new CloseSideNav());
      })
    );
}
